import { queryCtaElement, queryCtaElementsInElement } from '@/helpers/ctaShadow';
import { updateCtaHostPositionInWrapper } from '@/helpers/ctaHostPosition';
import { isMediaImageReady } from '@/helpers/mediaUtils';
import { applyCtaStyleToElements, applyCtaLinkIconVisibility } from '@/modules/parts/ctaButton';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { isReelLike } from '@/helpers/isReelLike';

// ストーリー型(circle)の cube 遷移中は、メディアの矩形が中間状態（縮んで中央寄り）になるため、
// この間に CTA を表示するとボタンが一瞬「上下中央」に出てから下部へ移動するちらつきになる。
// 遷移が完全に止まる（着地スライド確定）まで表示を保留する。リール型(square)はスライドと一緒に
// CTA も動かす仕様のため対象外。
const isCircleSlideTransitioning = () => (
  Boolean(window.storeeelItems?.isMobile)
  && window.storeeelItems?.frame === 'circle'
  && Boolean(getSwiperInstance()?.animating)
);

const isMediaReadyForCtaLayout = (mediaEl) => {
  if (!mediaEl) return false;

  if (mediaEl.tagName === 'IMG') {
    return isMediaImageReady(mediaEl);
  }

  if (mediaEl.tagName === 'VIDEO') {
    return Boolean(mediaEl.readyState >= 1);
  }

  return false;
};

export const updateCtaButtonAction = (wrapper) => {
  const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
  if (!activeSlide) return;
  // リール型・タイル型ではCTAをスライドと一緒に動かす
  const isMobileReel = window.storeeelItems?.isMobile && isReelLike(window.storeeelItems?.frame);

  const slide = wrapper?.classList?.contains?.('swiper-slide')
    ? wrapper
    : wrapper?.closest?.('.swiper-slide');

  // 呼び出し元のwrapperが「非アクティブスライド」だった場合、誤更新を防ぐために何もしない（念のため非表示）
  if (slide && slide !== activeSlide) {
    queryCtaElementsInElement('.ctaFrame', slide).forEach((el) => {
      if (!isMobileReel) el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    });
  }

  // loop（複製スライド）環境では、非アクティブスライドのCTAが前面に残って見えることがあるため、
  // 常にアクティブスライド以外は非表示にする。
  document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide').forEach((slideEl) => {
    if (slideEl === activeSlide) return;
    queryCtaElementsInElement('.ctaFrame', slideEl).forEach((el) => {
      if (!isMobileReel) el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    });
  });

  const root = activeSlide;
  const ctaButton = queryCtaElement('.ctaFrame', root);

  if (!ctaButton) {
    return;
  }

  // 更新前に「すでに表示中か」を記録しておく。新コンテンツにも CTA テキストがある場合、
  // いったん hidden にせず表示を維持してテキスト/位置だけ更新することで、
  // 画像→画像などの同一スライド内切替で CTA が一瞬消える点滅を防ぐ。
  const wasCtaVisible = window.getComputedStyle(ctaButton).visibility === 'visible';

  const textWrapper = ctaButton.querySelector('.text-wrapper');
  const storeeelItems = window.storeeelItems;
  const groupIdFromDom = root.querySelector('.slide-media-wrapper')?.getAttribute?.('group-id');
  const groupId = groupIdFromDom ?? window.iconId;
  const groups = storeeelItems?.contentGroups || [];
  const currentGroup = groups.find(g => String(g?.id) === String(groupId));
  const currentIndex = Number(window.moviePosition || 0);
  const currentContent = currentGroup?.contents?.[currentIndex];

  const ctaLink = (currentContent?.ctaLink || '').trim();
  const isFrequencyInsertContent = Boolean(currentContent?.isFrequencyInsertContent);
  const isFeedFolderContent = Boolean(currentContent?.isFeedFolderContent);
  const mediaId = isFrequencyInsertContent
    ? (currentContent?.frequencyInsertContentId || '')
    : (currentContent?.mediaAsset?.id || '');
  const mediaName = currentContent?.mediaAsset?.name || '';
  const mediaAssetType = currentContent?.mediaAsset?.type || '';
  const ctaButtonText = (currentContent?.ctaText || '').trim();
  const hasCtaText = ctaButtonText !== '';

  ctaButton.dataset.hasCtaText = hasCtaText ? 'true' : 'false';

  if (textWrapper) {
    textWrapper.textContent = ctaButtonText;
    // モバイルの同一CTA再利用時にも、content単位のCTAスタイルを追従させる
    applyCtaStyleToElements(currentContent, ctaButton, textWrapper);
  }
  ctaButton.setAttribute('ctaLink', ctaLink);
  applyCtaLinkIconVisibility(ctaLink, ctaButton);
  ctaButton.setAttribute('mediaId', mediaId);
  ctaButton.setAttribute('mediaName', mediaName);
  ctaButton.setAttribute('mediaAssetType', mediaAssetType);
  ctaButton.setAttribute('isFrequencyInsertContent', isFrequencyInsertContent ? 'true' : 'false');
  ctaButton.setAttribute('isFeedFolderContent', isFeedFolderContent ? 'true' : 'false');

  if (!hasCtaText) {
    ctaButton.style.visibility = 'hidden';
    return;
  }

  ctaButton.style.pointerEvents = 'auto';
  // モバイル+ストーリー型(circle)で、すでに表示中かつ cube 遷移中でない（＝同一スライド内のコンテンツ切替）なら、
  // 表示を維持したままテキスト/位置を更新して点滅を防ぐ。
  // cube 遷移中は中央ちらつき防止のため従来どおりいったん隠す。PC/リール型は対象外（従来動作を維持）。
  // ストーリー型のみ cube 遷移中ちらつき防止ロジックを適用
  const isMobileCircleCta = Boolean(window.storeeelItems?.isMobile) && window.storeeelItems?.frame === 'circle';
  const keepCtaVisibleInPlace = wasCtaVisible && isMobileCircleCta && !isCircleSlideTransitioning();
  if (!isMobileReel && !keepCtaVisibleInPlace) {
    ctaButton.style.visibility = 'hidden';
  }

  const getActiveMedia = () => (
    root.querySelector('img.storeeel-media-image.active, video.storeeel-movie.active')
    || Array.from(root.querySelectorAll('img.storeeel-media-image, video.storeeel-movie')).find((el) => {
      return getComputedStyle(el).display !== 'none';
    })
    || null
  );

  const showCtaIfNeeded = () => {
    // cube 遷移中は表示しない（中央ちらつき防止）。遷移後に retry / イベントで表示される。
    if (isCircleSlideTransitioning()) return false;
    const activeMedia = getActiveMedia();
    if (!isMediaReadyForCtaLayout(activeMedia)) return false;

    if (ctaButton.dataset.hasCtaText === 'true') {
      // 画像/動画の描画サイズ変化後にCTA座標を追従させる
      updateCtaHostPositionInWrapper(root.querySelector('.slide-media-wrapper') || root, { mediaEl: activeMedia });
      ctaButton.style.visibility = 'visible';
      ctaButton.style.pointerEvents = 'auto';
      return true;
    }

    return false;
  };

  const showCtaWithFallbackLayout = () => {
    if (ctaButton.dataset.hasCtaText !== 'true') return;
    const activeMedia = getActiveMedia();
    // メタデータ取得が遅い端末でもCTAが出ないままにならないよう、最後は現在の矩形で表示する
    updateCtaHostPositionInWrapper(root.querySelector('.slide-media-wrapper') || root, { mediaEl: activeMedia });
    ctaButton.style.visibility = 'visible';
    ctaButton.style.pointerEvents = 'auto';
  };

  // 画像/動画イベントに依存しすぎると、ループや差し替えのタイミングでCTAが表示されないことがあるため、
  // イベント取りこぼし対策として次tickでも確認するが、寸法未確定の間は表示しない。
  let retryCount = 0;
  let transitionWaitCount = 0;
  const retryShowCtaIfNeeded = () => {
    if (showCtaIfNeeded()) return;
    // cube 遷移中は着地（animating 解除）まで待つ。フォールバックで中央表示しないよう retry も進めない。
    // 万一 animating が解除されない端末に備えて上限（約1.5秒）を設ける。
    if (isCircleSlideTransitioning() && transitionWaitCount < 30) {
      transitionWaitCount += 1;
      window.setTimeout(retryShowCtaIfNeeded, 50);
      return;
    }
    if (retryCount >= 20 || ctaButton.dataset.hasCtaText !== 'true') {
      showCtaWithFallbackLayout();
      return;
    }
    retryCount += 1;
    window.setTimeout(retryShowCtaIfNeeded, 50);
  };
  window.setTimeout(retryShowCtaIfNeeded, 0);

  const activeMedia = getActiveMedia();
  if (activeMedia) {
    if (activeMedia.tagName === 'IMG') {
      // 画像の読み込み完了時
      if (isMediaReadyForCtaLayout(activeMedia)) {
        showCtaIfNeeded();
      } else {
        activeMedia.addEventListener('load', showCtaIfNeeded, { once: true });
      }
    } else if (activeMedia.tagName === 'VIDEO') {
      // 動画のメタデータ読み込み完了時
      if (isMediaReadyForCtaLayout(activeMedia)) {
        showCtaIfNeeded();
      } else {
        activeMedia.addEventListener('loadedmetadata', showCtaIfNeeded, { once: true });
      }
    }
  }
};
